import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { ASSETS } from './assets';
import { loadEnvironment } from './environment';
import { buildWorld, buildSkyBackground } from './world';

/**
 * Gestiona la escena 3D: renderer, cámara, controles, iluminación y bucle de render.
 * Expone `scene` para que fases posteriores añadan la pérgola, cotas, etc.
 */
export class SceneManager {
  readonly scene = new THREE.Scene();
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: OrbitControls;
  readonly sunLight: THREE.DirectionalLight;

  readonly hemiLight: THREE.HemisphereLight;
  readonly labelRenderer: CSS2DRenderer;

  private readonly container: HTMLElement;
  private readonly resizeObserver: ResizeObserver;
  private readonly clock = new THREE.Clock();
  private readonly updatables: Array<(dt: number) => void> = [];
  private envMap: THREE.Texture | null = null;
  private running = false;

  // Estado de ambiente (sol / noche / norte).
  private readonly dayBg: THREE.Texture;
  private readonly nightBg: THREE.Texture;
  private sunT = 0.62; // 0 mañana · 0.5 mediodía · 1 atardecer
  private northDeg = 0;
  private night = false;

  constructor(canvas: HTMLCanvasElement, container: HTMLElement) {
    this.container = container;

    // --- Renderer: ACESFilmic + sRGB, como el configurador de referencia ---
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // --- Renderer de etiquetas HTML (cotas tipo plano técnico) ---
    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.domElement.className = 'labels-layer';
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0';
    this.labelRenderer.domElement.style.left = '0';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    this.container.appendChild(this.labelRenderer.domElement);

    // --- Cámara ---
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
    this.camera.position.set(7, 4.5, 9);

    // --- Controles orbitales con límites ---
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.target.set(0, 1.2, 0);
    this.controls.minDistance = 3;
    this.controls.maxDistance = 30;
    this.controls.maxPolarAngle = THREE.MathUtils.degToRad(88); // no bajar del suelo
    this.controls.minPolarAngle = THREE.MathUtils.degToRad(5);
    this.controls.update();

    // --- Fondo de cielo (día neutro / noche) ---
    this.dayBg = buildSkyBackground();
    this.nightBg = buildSkyBackground(true);
    this.scene.background = this.dayBg;

    // --- Luz hemisférica de relleno (cielo/suelo) ---
    this.hemiLight = new THREE.HemisphereLight(0xffffff, 0xd6dbe0, 0.55);
    this.scene.add(this.hemiLight);

    // --- Sol direccional (sombras) complementando la IBL del HDRI ---
    this.sunLight = new THREE.DirectionalLight(0xffffff, 1.6);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(2048, 2048);
    this.sunLight.shadow.camera.near = 1;
    this.sunLight.shadow.camera.far = 50;
    const s = 16;
    this.sunLight.shadow.camera.left = -s;
    this.sunLight.shadow.camera.right = s;
    this.sunLight.shadow.camera.top = s;
    this.sunLight.shadow.camera.bottom = -s;
    this.sunLight.shadow.bias = -0.0002;
    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target);
    this.applySun();

    // --- Entorno: terreno neutro + casa + árboles (?notrees para QA) ---
    this.scene.add(buildWorld(!new URLSearchParams(window.location.search).has('notrees')));

    // --- Resize reactivo al contenedor (panel 65%) ---
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.resize();
  }

  /** Centra la órbita en una estructura de altura `totalHeight` (m). */
  frameOn(totalHeight: number): void {
    this.controls.target.set(0, totalHeight * 0.45, 0);
    this.controls.update();
  }

  /** Posición del sol: 0 = mañana, 0.5 = mediodía, 1 = atardecer. */
  setSunPosition(t: number): void {
    this.sunT = Math.min(1, Math.max(0, t));
    this.applySun();
  }

  /** Dirección norte en grados (0–360): rota el azimut del sol. */
  setNorth(deg: number): void {
    this.northDeg = deg;
    this.applySun();
  }

  /** Modo noche (para apreciar la iluminación LED de la pérgola). */
  setNight(on: boolean): void {
    this.night = on;
    this.scene.background = on ? this.nightBg : this.dayBg;
    this.hemiLight.intensity = on ? 0.07 : 0.55;
    this.renderer.toneMappingExposure = on ? 0.82 : 1.0;
    this.scene.environmentIntensity = on ? 0.12 : 1.0;
    this.applySun();
  }

  get isNight(): boolean {
    return this.night;
  }

  /** Coloca el sol según hora (sunT), norte (northDeg) y modo noche. */
  private applySun(): void {
    const t = this.sunT;
    const noon = Math.sin(t * Math.PI); // 0 en extremos, 1 al mediodía
    const elevDeg = noon * 56 + 8; // 8°..64°
    const azDeg = -82 + t * 164 + this.northDeg; // este → oeste + offset norte
    const el = THREE.MathUtils.degToRad(elevDeg);
    const az = THREE.MathUtils.degToRad(azDeg);
    const r = 22;
    this.sunLight.position.set(
      Math.cos(el) * Math.sin(az) * r,
      Math.sin(el) * r,
      Math.cos(el) * Math.cos(az) * r,
    );
    this.sunLight.target.position.set(0, 0, 0);
    this.sunLight.target.updateMatrixWorld();

    if (this.night) {
      this.sunLight.intensity = 0.22;
      this.sunLight.color.set(0x9fb4d8); // luz de luna
    } else {
      this.sunLight.intensity = 1.0 + noon * 0.9;
      const warm = new THREE.Color(0xffcf9c);
      const white = new THREE.Color(0xffffff);
      this.sunLight.color.copy(warm).lerp(white, noon);
    }
  }

  /** Carga el HDRI de forma asíncrona (IBL + cielo de fondo). */
  async loadEnvironment(): Promise<void> {
    this.envMap = await loadEnvironment(this.renderer, this.scene, ASSETS.hdri);
  }

  /** Arranca el bucle de render. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.renderer.setAnimationLoop(this.tick);
  }

  /** Detiene el bucle de render (sin liberar recursos). */
  stop(): void {
    this.running = false;
    this.renderer.setAnimationLoop(null);
  }

  /** Registra una función a ejecutar en cada frame (delta en segundos). */
  addUpdatable(fn: (dt: number) => void): void {
    this.updatables.push(fn);
  }

  private tick = (): void => {
    const dt = this.clock.getDelta();
    for (const fn of this.updatables) fn(dt);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    this.labelRenderer.render(this.scene, this.camera);
  };

  /** Ajusta renderer y cámara al tamaño del contenedor. */
  resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (w === 0 || h === 0) return;
    this.renderer.setSize(w, h, false);
    this.labelRenderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.running = false;
    this.renderer.setAnimationLoop(null);
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.envMap?.dispose();
    this.renderer.dispose();
  }
}
